import React from 'react';
import './search-result-card.less';

import CheckIcon from '@atlaskit/icon/glyph/check';
import CommentIcon from '@atlaskit/icon/glyph/comment';
import LikeIcon from '@atlaskit/icon/glyph/like';
import { AtlasLink } from '../atlas-link';
import { Breadcrumb, TitleSection, TicketType } from '.';
import { highlightSearchTerm } from '../../utils/highlight-search-result-keyword';
import analytics from '../../utils/analytics';

export interface SearchResultCardProps {
    id: string;
    ticketType?: TicketType;
    breadcrumbs?: string[];
    title: string;
    description: string;
    dateModified?: string;
    answerAccepted?: boolean;
    views?: number;
    comments?: number;
    markedUseful?: number;
    href: string;
    platform?: string;
    searchTerm: string;
    position: number;
}

export const SearchResultCard: React.FC<SearchResultCardProps> = ({
    id,
    breadcrumbs,
    title,
    description,
    dateModified,
    answerAccepted,
    views,
    comments,
    markedUseful,
    ticketType,
    href,
    platform,
    searchTerm,
    position
}: SearchResultCardProps) => {
    const isFooterVisible = answerAccepted || views || comments || markedUseful;
    const timeRendered = Date.now();

    const formattedDate = dateModified
        ? new Date(dateModified).toLocaleDateString()
        : undefined;

    const renderAnswerAccepted = () => {
        if (answerAccepted) {
            return (
                <span
                    className="answer-accepted background-success"
                    data-testid="answer-accepted"
                >
                    <span className="check-icon">
                        <CheckIcon size="small" label="Answer accepted" />
                    </span>
                    <span>Answer Accepted</span>
                </span>
            );
        }
        return null;
    };

    const renderViews = () => {
        if (views !== undefined) {
            return <span data-testid="views">{views} views</span>;
        }
        return null;
    };

    const renderComments = () => {
        if (comments !== undefined) {
            return (
                <span className="comments" data-testid="comments">
                    <CommentIcon size="small" label="Comments" />
                    <span>{comments}</span>
                </span>
            );
        }
        return null;
    };

    const renderMarkedUseful = () => {
        if (markedUseful !== undefined) {
            return (
                <span className="marked-useful" data-testid="marked-useful">
                    <LikeIcon size="small" label="marked-useful" />
                    <span>{markedUseful}</span>
                </span>
            );
        }
        return null;
    };

    return (
        <AtlasLink
            className="search-result-card search-result-card__card-button-wrapper"
            data-testid="search-result-card"
            href={href}
            target="_blank"
            text={title}
            onClick={() => {
                analytics.trackEvent('search result', {
                    // @ts-ignore
                    event: 'clicked',
                    eventComponent: 'search-result-card',
                    actionSubjectId: 'link',
                    category: 'search',
                    action: 'clicked',
                    label: 'search result card',
                    position: position,
                    searchTerm: searchTerm,
                    linkUrl: href,
                    contentId: id,
                    timeToClick: (Date.now() - timeRendered) / 1000
                });
            }}
        >
            <div className="search-result-card__card-container">
                <div className="left-section">
                    <Breadcrumb breadcrumbs={breadcrumbs} />
                    <div className="card-content text-gray">
                        <TitleSection
                            ticketType={ticketType}
                            title={title}
                            searchTerm={searchTerm}
                        />
                        <p className="card-description">
                            {highlightSearchTerm(searchTerm, description)}
                        </p>
                        {formattedDate && (
                            <p
                                className="font-weight-600"
                                data-testid="datemodified"
                            >
                                {formattedDate}
                            </p>
                        )}
                        {isFooterVisible && (
                            <div
                                className="card-footer"
                                data-testid="card-footer"
                            >
                                {renderAnswerAccepted()}
                                {renderViews()}
                                {renderComments()}
                                {renderMarkedUseful()}
                            </div>
                        )}
                    </div>
                </div>
                <div className="right-section text-gray">
                    <span className="deployment font-weight-600">
                        {platform}
                    </span>
                </div>
            </div>
        </AtlasLink>
    );
};
